import { useMemo, useRef, useCallback } from 'react';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import { post, get, del, put } from '../utils/api';

export async function getFlows() {
  const res = await get('/flows');
  return res?.data;
}

export async function getFlow({ id, includeCurrentRuns, includeTriggers }) {
  const res = await get(`/flows/${id}`, {
    params: {
      include_current_runs: includeCurrentRuns ? 1 : 0,
      include_triggers: includeTriggers ? 1 : 0,
    },
  });
  return res?.data;
}

export async function createFlow({ name, description, data }) {
  const res = await post('/flows', { name, description, data });
  return res?.data;
}

export async function updateFlow({ id, name, description, data, status }) {
  const res = await put(`/flows/${id}`, { name, description, data, status });
  return res?.data;
}

export async function deleteFlow({ id }) {
  const res = await del(`/flows/${id}`);
  return res?.data;
}

export async function uploadFlowFile({ id, form, mimeType, params }) {
  const res = await post(`/flows/${id}/file`, form, {
    params: {
      ...params,
      extract_variables: true,
    },
    headers: {
      'X-Document-Content-Type': mimeType,
    },
  });

  return res?.data;
}

export async function runFlow({ flowId, inputs }) {
  const res = await post(`/flows/${flowId}/run`, { inputs });
  return res?.data;
}

export async function generateFlow({ prompt, nodes, signal }) {
  const res = await post('/flows/generate', { prompt, nodes }, { signal });
  return res?.data;
}

export async function getFlowRuns() {
  const res = await get('/flow-runs');
  return res?.data?.runs;
}

export function useFlowRunsQuery({ enabled }) {
  return useQuery('flow-runs', getFlowRuns, {
    refetchInterval: 1000 * 30,
    enabled,
  });
}

export function useFlowsQuery() {
  return useQuery('flows', getFlows);
}

export function useFlowQuery({
  id,
  includeCurrentRuns = false,
  includeTriggers = false,
}) {
  return useQuery(
    ['flows', id],
    () => getFlow({ id, includeCurrentRuns, includeTriggers }),
    {
      enabled: !!id,
    }
  );
}

export function useFlowActions(flow) {
  const { id } = flow;
  const queryClient = useQueryClient();

  const updateFlowMutation = useMutation(
    (data) => updateFlow({ ...data, id }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('flows');
      },
    }
  );

  const deleteFlowMutation = useMutation(() => deleteFlow({ id }), {
    onSuccess: () => {
      queryClient.invalidateQueries('flows');
    },
  });

  const runFlowMutation = useMutation(
    (data) => runFlow({ flowId: id, ...data }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['flows', id]);
        queryClient.invalidateQueries('flows');
        queryClient.invalidateQueries('flow-runs');
      },
    }
  );

  return {
    updateFlow: updateFlowMutation.mutateAsync,
    deleteFlow: deleteFlowMutation.mutateAsync,
    runFlow: runFlowMutation.mutateAsync,
  };
}

export function useFlowsActions() {
  const queryClient = useQueryClient();

  const createFlowMutation = useMutation(createFlow, {
    onSuccess: () => {
      queryClient.invalidateQueries('flows');
    },
  });

  return {
    createFlow: createFlowMutation.mutateAsync,
  };
}
